import React from "react";
import { Helmet } from "react-helmet-async";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Newspaper,
  CalendarDays,
  ArrowRight,
  TrendingUp,
  BookOpen,
  BadgePoundSterling,
  FileText,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getPostsByProductKey, getPostSlug } from "./ProductBlogCategoryPage.jsx";

const HERO_SRC = "/blogs_News.webp";

const BTL_BLOG_BASE = "/blog/buy-to-let-mortgage";

const featuredPost = {
  title: "Houses in Multiple Occupation (HMOs): Are They Worth It in 2026?",
  date: "2026",
  excerpt:
    "HMOs can offer higher rental yields than standard buy-to-let—but licensing, management and specialist finance matter. A 2026 guide for UK landlords.",
  to: `${BTL_BLOG_BASE}/houses-in-multiple-occupation-hmos-are-they-worth-it-in-2026`,
  imageSrc: "/Blog_Images/HMO_1.webp",
};

function mapBtlPostToCard(post) {
  return {
    title: post.title,
    date: post.date,
    excerpt: post.excerpt,
    category: "BTL Guides",
    to: `${BTL_BLOG_BASE}/${getPostSlug(post)}`,
    imageSrc: post.imageSrc,
  };
}

const posts = getPostsByProductKey("buy-to-let-mortgage")
  .filter((post) => post.content && post.date !== "Coming soon")
  .slice(0, 12)
  .map(mapBtlPostToCard);

const PRODUCT_ARTICLE_LINKS = [
  { key: "buy-to-let-mortgage", label: "Buy to Let", to: "/blog/buy-to-let-mortgage" },
  { key: "invoice-finance", label: "Invoice finance", to: "/blog/invoice-finance" },
  { key: "asset-finance", label: "Asset finance", to: "/blog/asset-finance" },
  { key: "complex-btl", label: "Complex BTL", to: "/blog/complex-btl" },
  { key: "commercial", label: "Commercial", to: "/blog/commercial" },
  { key: "semi-commercial", label: "Semi-commercial", to: "/blog/semi-commercial" },
  { key: "bridging", label: "Bridging", to: "/blog/bridging" },
  { key: "development", label: "Development", to: "/blog/development" },
];

export default function BlogsNews() {
  const productArticleLinks = React.useMemo(
    () =>
      PRODUCT_ARTICLE_LINKS.map((item) => ({
        ...item,
        articleCount: getPostsByProductKey(item.key).length,
      })),
    []
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = HERO_SRC;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  React.useEffect(() => {
    const schemaId = "blogs-news-collection-schema";
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.id = schemaId;
      schemaScript.type = "application/ld+json";
      document.head.appendChild(schemaScript);
    }
    schemaScript.text = JSON.stringify(collectionSchema);
  }, []);

  const title = "BTL Mortgage Blogs & News UK | Dynesourcer";
  const description =
    "Read buy-to-let mortgage blogs, lender criteria updates, affordability guides, and product insights for UK landlords, borrowers, and property investors.";
  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}/blog`
      : "https://dynesourcer.co.uk/blog";

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "BTL Mortgage Blogs and News | Dynesourcer",
    url: "https://dynesourcer.co.uk/blog",
    description:
      "Read blogs, guides and news on buy to let mortgages, lender criteria, rates, affordability and mortgage sourcing in the UK.",
    hasPart: posts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date === "Coming soon" ? undefined : post.date,
      publisher: {
        "@type": "Organization",
        name: "Dynesourcer",
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <main className="min-h-screen bg-white text-slate-800">
        <TopNav />

        <section className="relative h-[500px] w-full overflow-hidden hero-landscape-responsive">
          <img
            src={HERO_SRC}
            alt="Buy to let mortgage blogs and news in the UK"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-sky-900/60" />
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
            <h1 className="text-4xl font-black tracking-tight md:text-6xl">
              Buy to Let Mortgage Blogs & News
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/90 md:text-lg">
              Read practical guides, market insights, lender criteria updates and
              mortgage sourcing content for UK landlords, borrowers, property
              investors and brokers.
            </p>

            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg"
              >
                <Search className="h-4 w-4" />
                Source BTL Products
              </Link>
              <Link
                to="/buy-to-let-mortgage-calculator"
                className="inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
              >
                <FileText className="h-4 w-4" />
                Use BTL Calculator
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Mortgage Insights for UK Borrowers and Property Investors
              </h2>
              <p className="mt-4 text-slate-600 md:text-lg">
                This page brings together blogs and news covering buy to let
                mortgages, lender criteria, affordability, rates, fees and
                product sourcing. As Dynesourcer grows, this section will also
                include content for other mortgage and property finance products,
                helping users move from research to action with clearer,
                product-led insight.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-3">
              <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">Market & Product Insights</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Follow updates on BTL mortgage products, rates, lender trends
                  and product comparison topics relevant to UK borrowers.
                </p>
              </div>

              <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <BookOpen className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">Practical Borrower Guides</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Learn how to compare mortgage products, understand lender
                  criteria and navigate the buy to let borrower journey.
                </p>
              </div>

              <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <BadgePoundSterling className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">Affordability & Criteria</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Explore how fees, rates, EPC rules, taxpayer type, rental
                  stress and other filters can influence product matching.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-sky-50 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
              <article className="rounded-2xl border border-sky-100 bg-white p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-sm font-semibold text-sky-700">
                  <Newspaper className="h-4 w-4" />
                  Featured article
                </div>

                <h2 className="mt-5 text-3xl font-black text-slate-900 md:text-4xl">
                  {featuredPost.title}
                </h2>

                <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-500">
                  <CalendarDays className="h-4 w-4" />
                  {featuredPost.date}
                </div>

                <p className="mt-5 max-w-3xl text-slate-600">
                  {featuredPost.excerpt}
                </p>

                {featuredPost.imageSrc ? (
                  <img
                    src={featuredPost.imageSrc}
                    alt={`${featuredPost.title} preview`}
                    className="mt-5 w-full max-w-xl rounded-xl object-cover max-h-56"
                    loading="lazy"
                  />
                ) : null}

                <p className="mt-4 text-slate-600">
                  Our content is designed to help borrowers understand not just
                  headline rates, but also the wider factors that shape product
                  suitability, including arrangement fees, product-term costs,
                  lender policy filters and borrower profile criteria.
                </p>

                <Link
                  to={featuredPost.to}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-5 py-3 font-semibold text-white transition-all hover:bg-sky-600"
                >
                  Read article <ArrowRight className="h-4 w-4" />
                </Link>
              </article>

              <aside className="rounded-2xl border border-sky-100 bg-white p-8 shadow-sm">
                <h3 className="text-2xl font-black text-slate-900">Popular topics</h3>
                <p className="mt-3 text-slate-600">
                  This section will expand into topic clusters to help users find
                  the right content faster.
                </p>

                <div className="mt-6">
                  <h4 className="text-sm font-bold text-slate-900">By product</h4>
                  <ul className="mt-3 space-y-2">
                    {productArticleLinks.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className="flex items-center justify-between rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-sky-100"
                        >
                          <span>{`${item.label} (${item.articleCount})`}</span>
                          <ArrowRight className="h-4 w-4 text-sky-600" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 rounded-xl bg-sky-900 p-6 text-white">
                  <h4 className="text-xl font-black">Start with a live search</h4>
                  <p className="mt-2 text-sm text-white/90">
                    Move from reading guides to comparing real BTL mortgage
                    products using Dynesourcer’s sourcing flow.
                  </p>
                  <Link
                    to="/sourcing/buy-to-let-mortgage-sourcing"
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-4 py-2 font-semibold text-white transition-all hover:bg-sky-400"
                  >
                    Source BTL products <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Latest Blogs and Posts
              </h2>
              <p className="mt-3 text-slate-600">
                Explore published buy-to-let guides covering HMOs, deposits, rental yield,
                lender criteria, affordability and borrower guidance in the UK.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {posts.map((post) => (
                <article
                  key={post.title}
                  className="rounded-xl border border-sky-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-lg"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <Newspaper className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                      {post.category}
                    </span>
                  </div>

                  <h3 className="text-xl font-black text-slate-900">{post.title}</h3>

                  <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-500">
                    <CalendarDays className="h-4 w-4" /> {post.date}
                  </div>

                  {post.imageSrc ? (
                    <img
                      src={post.imageSrc}
                      alt={`${post.title} preview`}
                      className="mt-3 h-36 w-full rounded-lg object-cover"
                      loading="lazy"
                    />
                  ) : null}

                  <p className="mt-3 text-sm leading-6 text-slate-600">{post.excerpt}</p>

                  <Link
                    to={post.to}
                    className="mt-5 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600"
                  >
                    Read article <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-50 py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <div className="rounded-2xl border border-sky-100 bg-white p-8 text-center shadow-sm md:p-10">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                What you will find here
              </h2>
              <p className="mt-4 text-slate-600">
                This content hub is being built to support borrowers, landlords,
                brokers and property investors with useful, search-friendly
                guidance around mortgage sourcing and product evaluation.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <div className="rounded-xl bg-sky-50 p-5">
                  <h3 className="font-bold text-slate-900">Guides</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Clear explanations of BTL mortgage products, rates, fees,
                    criteria and borrower workflows.
                  </p>
                </div>
                <div className="rounded-xl bg-sky-50 p-5">
                  <h3 className="font-bold text-slate-900">News</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Updates on mortgage product trends, criteria changes and
                    relevant market developments.
                  </p>
                </div>
                <div className="rounded-xl bg-sky-50 p-5">
                  <h3 className="font-bold text-slate-900">Platform insights</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Content showing how Dynesourcer helps users compare, source,
                    shortlist and apply online.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-sky-500 py-14 text-center text-white">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-black md:text-4xl">
              Ready to move from insights to action?
            </h2>
            <p className="mt-3 text-white/90">
              Explore real buy to let mortgage products, compare key costs and
              continue into shortlisting and application.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-sky-700 transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-lg"
              >
                Source BTL products now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/buy-to-let-mortgage-calculator"
                className="inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
              >
                Use BTL calculator
              </Link>
            </div>
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}